import { Slider, Tabs } from "antd";
import utils from "../../../utils";
import styles from "./index.module.scss";
import { useMusicContext } from "..";
import { useT } from "../../../i18n/hooks";
import Marquee from "react-fast-marquee";
import {
  RetweetOutlined,
  StepBackwardFilled,
  StepForwardFilled,
  CaretRightFilled,
  PauseOutlined,
  DownloadOutlined,
  CheckOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import classNames from "classnames";
import Icons from "../../../components/Icons";
import ErrorBoundary from "../../../components/ErrorBoundary";
import { AntdCus } from "../../../components/AntdCus";
import { Popup } from "../../../components";

export default () => {
  return (
    <ErrorBoundary>
      <Inside />
    </ErrorBoundary>
  );
};

function Inside() {
  const t = useT();
  const { setPlayerShow } = useMusicContext();
  return (
    <div className={styles.root}>
      <Popup onClick={() => setPlayerShow(false)}>
        <div className={styles.title}>{t("music.music")}</div>
        <VolumeBox />
        <ControlBox />
        <MusicBox />
      </Popup>
    </div>
  );
}

let tempVolume = 0;

function VolumeBox() {
  const t = useT();
  const { volume, setVolume } = useMusicContext();
  return (
    <div className={styles.volumeBox}>
      <span className={styles.label}>{t("music.volume")}</span>
      <Slider
        className={styles.slider}
        step={1}
        value={volume}
        onChange={setVolume}
      />
      <span
        className={styles.volumeButton}
        onClick={() => {
          if (volume === 0) {
            setVolume(tempVolume);
          } else {
            tempVolume = volume;
            setVolume(0);
          }
        }}
      >
        {volume === 0 ? <Icons.VolumeMuted /> : <Icons.Volume />}
      </span>
    </div>
  );
}

function ControlBox() {
  const t = useT();
  const {
    currentMusic,
    playing,
    pause,
    play,
    prev,
    next,
    playlist,
    changePlayMode,
    playMode,
    currentMusicInstance = {
      currentTime: 0,
    },
    seek,
  } = useMusicContext();
  const formatTime = (time: number) => {
    return utils.formatMinuteTimeSpan(time * 1000);
  };
  return (
    <div className={styles.controlBox}>
      <Marquee
        className={classNames(styles.name, { [styles.hide]: !playing })}
        play={playing}
      >
        {currentMusic?.name || "***"}
      </Marquee>
      <span className={classNames(styles.name, { [styles.hide]: playing })}>
        {currentMusic?.name || "***"}
      </span>

      <div className={styles.sliderBox}>
        <span className={styles.time}>
          {formatTime(currentMusicInstance.currentTime || 0)}
        </span>
        <Slider
          className={styles.slider}
          min={0}
          max={currentMusic?.duration || 0}
          step={1}
          tooltip={{ formatter: value => formatTime(value || 0) }}
          value={currentMusicInstance.currentTime || 0}
          onChange={seek}
        />
        <span className={styles.time}>
          {formatTime(currentMusic?.duration || 0)}
        </span>
      </div>

      <div className={styles.controlBottom}>
        <div
          className={styles.btn}
          style={{ cursor: "pointer" }}
          onClick={changePlayMode}
        >
          <span className={styles.btnTop}>
            {playMode === "list" ? (
              <RetweetOutlined />
            ) : playMode === "single" ? (
              <Icons.RepeatOne />
            ) : (
              <Icons.Shuffle />
            )}
          </span>
          <span className={styles.btnLabel}>
            {t(
              playMode === "list"
                ? "music.cycle"
                : playMode === "single"
                  ? "music.repeat"
                  : "music.shuffle"
            )}
          </span>
        </div>
        <div className={styles.actions}>
          <div className={styles.action} onClick={prev}>
            <StepBackwardFilled />
          </div>
          <div
            className={styles.action}
            onClick={() => (playing ? pause() : play())}
          >
            {playing ? (
              <PauseOutlined style={{ fontSize: "1.5rem" }} />
            ) : (
              <CaretRightFilled
                style={{ fontSize: "1.5rem", marginLeft: ".15rem" }}
              />
            )}
          </div>
          <div className={styles.action} onClick={next}>
            <StepForwardFilled />
          </div>
        </div>
        <div className={styles.btn}>
          <span
            className={styles.btnTop}
            style={{ color: "var(--design-text-color, #fff)", opacity: "0.6" }}
          >
            {playlist.length}
          </span>
          <span className={styles.btnLabel}>{t("music.cached")}</span>
        </div>
      </div>
    </div>
  );
}

function MusicBox() {
  const t = useT();
  return (
    <div className={styles.musicBox}>
      <AntdCus
        Element={Tabs}
        className={styles.tabs}
        centered
        size="large"
        items={[
          {
            key: "0",
            label: t("music.systemMusic"),
            children: <MusicList />,
          },
          {
            key: "1",
            label: t("music.myMusic"),
            children: <MusicList cached />,
          },
        ]}
      />
    </div>
  );
}

function MusicList({ cached = false }) {
  const { playlist, musicList, currentMusic } = useMusicContext();
  const list = cached ? playlist : musicList;
  return (
    <div className={styles.musicList}>
      {list.map((item: any, index: number) => (
        <MusicItem
          key={item.id}
          index={index}
          data={item}
          isPlaying={currentMusic && currentMusic?.id === item.id}
        />
      ))}
    </div>
  );
}

function MusicItem({
  data,
  index,
  isPlaying = false,
}: {
  data: any;
  index: number;
  isPlaying?: boolean;
}) {
  const { playMusic, cacheMusic } = useMusicContext();
  return (
    <div
      className={classNames(styles.musicItem, {
        [styles.playing]: isPlaying,
      })}
      onClick={() => playMusic(data)}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        <span className={styles.index}>
          {isPlaying ? <Icons.Music /> : index + 1}
        </span>
        <span className={styles.name}>{data.name}</span>
        <span className={styles.size}>
          {utils.formatLocaleNumber(data.size / 1024 / 1024, null, 1, 1)}M
        </span>
      </div>
      <span
        className={styles.status}
        onClick={e => {
          if (data.cached) return;
          cacheMusic(data);
          e.stopPropagation();
          e.stopPropagation();
        }}
      >
        {data.caching ? (
          <LoadingOutlined />
        ) : data.cached ? (
          <CheckOutlined />
        ) : (
          <DownloadOutlined />
        )}
      </span>
    </div>
  );
}
